import { useEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { AgentRunModel } from "@/api/client/messages"

import {
  AGENT_RUNS_QUERY_KEY,
  isActiveAgentRunStatus,
  useAgentRunsQuery,
} from "@/features/ai-explorer/hooks/use-agent-runs-query"

import { SETTLED_RUN_RECHECK_MS, trustedSessionRun } from "../utils/agent-presence-utils"

/**
 * The open thread's run, as presence and the busy gate should read it (#3207).
 *
 * Busy is derived from the run registry's STATUS, re-read at the moments it
 * can change, instead of from whichever snapshot happened to be cached when the
 * turn's last event arrived:
 *
 * - when this tab watches its turn settle, the registry is re-read at once and
 *   any older "executing" snapshot is withheld until that read lands;
 * - while the registry still claims a run the local chat is no longer running,
 *   it is re-read every few seconds, so a terminal state the backend wrote late
 *   clears the presence line on the next poll rather than on a reload.
 */
export function useAgentSessionRun(sessionId: string | null | undefined, localBusy: boolean) {
  const queryClient = useQueryClient()
  const agentRunsQuery = useAgentRunsQuery()
  const [settledAt, setSettledAt] = useState<number | null>(null)
  const wasBusyRef = useRef(localBusy)

  useEffect(() => {
    if (wasBusyRef.current && !localBusy) {
      setSettledAt(Date.now())
      void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
    }
    wasBusyRef.current = localBusy
  }, [localBusy, queryClient])

  const registryRun = useMemo<AgentRunModel | null>(
    () =>
      sessionId ? (agentRunsQuery.data?.find((run) => run.session_id === sessionId) ?? null) : null,
    [agentRunsQuery.data, sessionId]
  )
  const currentSessionAgentRun = trustedSessionRun(registryRun, {
    snapshotAt: agentRunsQuery.dataUpdatedAt,
    settledAt,
  })
  const isCurrentSessionAgentRunActive =
    currentSessionAgentRun !== null && isActiveAgentRunStatus(currentSessionAgentRun.status)

  const registryClaimsActive = isActiveAgentRunStatus(registryRun?.status)
  const { refetch } = agentRunsQuery
  useEffect(() => {
    if (!registryClaimsActive || localBusy) return
    const timer = setInterval(() => void refetch(), SETTLED_RUN_RECHECK_MS)
    return () => clearInterval(timer)
  }, [registryClaimsActive, localBusy, refetch])

  return { currentSessionAgentRun, isCurrentSessionAgentRunActive }
}
